import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import "../../../assets/styles/rdcstatus.css";
import { commonService } from "../../../services/commonService";
import Pagination from '../../../components/Common/Pagination';
import Loader from '../../../components/Common/Loader';
import { TextAlignCenter } from "lucide-react";

const VDCStatus: React.FC = () => {
    const navigate = useNavigate();
    const [downloads, setDownloads] = useState<any[]>([]);
    const [pageDetail, setPageDetail] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);

    // Loader for row download
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    // Popup error message
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const payload = {
                page: 1,
                page_size: pageSize,
            };

            try {
                const response = await commonService.vdcList(payload);
                setDownloads(response?.data);

                const page_data = {
                    page: response.page,
                    total_pages: response.total_pages,
                    total_records: response.total,
                    page_size: response.page_size,
                };
                setPageDetail(page_data);
            } catch (err) {
                setErrorMsg("Failed to load records.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const fetchData = async (newPage: any) => {
        const payload = {
            page: newPage,
            page_size: pageSize,
        };

        try {
            const response = await commonService.vdcList(payload);
            setDownloads(response?.data);

            const page_data = {
                page: response.page,
                total_pages: response.total_pages,
                total_records: response.total,
                page_size: response.page_size,
            };
            setPageDetail(page_data);
        } catch (err) {
            setErrorMsg("Failed to load records.");
        } finally {
            setLoading(false);
        }
    };

    const downloadData = async (request_id: string, filename: string, value: string) => {
        if (!request_id) return;

        try {
            setDownloadingId(request_id);
            let response;
            if (value === "all") {
                response = await commonService.vdcDownload(request_id);

            } else if (value === "Error") {
                response = await commonService.vdcDownloadError(request_id);
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            link.click();
            // Cleanup
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            setErrorMsg(error.message || "Failed to download data.");
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <MainLayout>
            <div className="rde-downloads-page" style={{ backgroundColor: "#f0f8ff" }}>

                {/* Header */}
                <div className="rde-header">
                    <div className="rde-back-button-container">
                        <button onClick={() => navigate(-1)} className="rde-back-button">
                            <BackIcon />
                            Back
                        </button>
                    </div>
                    <h4 className="rde-title">VDC Upload Status</h4>
                </div>

                {/* Main Table */}
                <div>
                    {loading ? (
                        <Loader
                            text='Loading...' />
                    ) : (
                        <div className="mt-3 rt-tablewrap">
                            <table className="table table-bordered table-striped text-center">
                                <thead className="table-primary">
                                    <tr>
                                        <th>Sl No</th>
                                        <th>Request ID</th>
                                        <th>File Name</th>
                                        <th>Upload Count</th>
                                        <th>Success Count</th>
                                        <th>Failed Count</th>
                                        <th>Status</th>
                                        <th>Created At</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {downloads.length > 0 ? (
                                        downloads.map((item, index) => (
                                            <tr key={item.request_id || index}>
                                                <td data-label="Sl No"> {((pageDetail.page - 1) * pageDetail.page_size) + (index + 1)}</td>
                                                <td data-label="Request ID">VDC_{item.id}</td>
                                                <td data-label="File Name">{item.file_name}</td>
                                                <td data-label="Upload Count">{item.data_count}</td>
                                                <td data-label="Success Count">{item.success_count}</td>
                                                <td data-label="Failed Count">{item.failed_count}</td>
                                                <td data-label="Status">
                                                    <span style={{
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        backgroundColor: item.status == 'completed' ? "#218d3aff" : "rgba(213, 74, 66, 1)",
                                                        color: "#ffffff",
                                                    }}>
                                                        {item.status.charAt(0).toUpperCase() +
                                                            item.status.slice(1).toLowerCase()}
                                                    </span>
                                                </td>
                                                <td data-label="Created At">
                                                    {new Date(item.created_at).toLocaleString("en-IN", {
                                                        timeZone: "Asia/Kolkata",
                                                        year: "numeric",
                                                        month: "2-digit",
                                                        day: "2-digit",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        second: "2-digit",
                                                    })}
                                                </td>
                                                <td data-label="Action">
                                                    <select
                                                        className="vdc-action-select"
                                                        onChange={(e) => {
                                                            if (e.target.value !== "") {
                                                                downloadData(item.id, item.file_name, e.target.value);
                                                                e.target.value = "";
                                                            }
                                                        }}
                                                    >
                                                        <option value="">⚙️ Action</option>
                                                        <option value="all">⬇️ Download Full CSV</option>
                                                        {(item.failed_count > 0 && item.status === "completed") || item.status === 'failed' &&
                                                            <option value="Error">⚠️ Download Failed CSV</option>
                                                        }

                                                    </select>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={9}>No download records found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <Pagination
                    page={pageDetail.page}
                    total_pages={pageDetail.total_pages}
                    total_records={pageDetail.total_records}
                    page_size={pageDetail.page_size}
                    onPageChange={(newPage) => fetchData(newPage)}
                />

                {/* Error Popup */}
                {errorMsg && (
                    <div className="popup-overlay">
                        <div className="popup" style={{ textAlign: "center" }}>
                            <p>{errorMsg}</p>
                            <button onClick={() => setErrorMsg(null)} style={{
                                backgroundColor: "#61A1D4",
                                padding: "3px 10px",
                                borderRadius: "7px",
                                textAlign: "center",
                                color: "white",
                                border: "none",
                                cursor: "pointer"
                            }}>Close</button>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default VDCStatus;
